export const SLIP_MAX_FILE_BYTES = 5 * 1024 * 1024
export const SLIP_MAX_FILE_MB = 5
export const SLIP_COMPRESS_IF_LARGER_THAN_BYTES = 768 * 1024
export const SLIP_MAX_IMAGE_DIMENSION = 2048
export const SLIP_MIN_FILE_BYTES = 1024

export const SLIP_ALLOWED_MIME_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/gif',
  'image/webp',
] as const

export const SLIP_ALLOWED_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.gif', '.webp'] as const

export const SLIP_UPLOAD_HINT = `รองรับ JPG, PNG, GIF, WebP ขนาดไม่เกิน ${SLIP_MAX_FILE_MB}MB`

const SUSPICIOUS_FILENAME_TOKENS = [
  'javascript',
  'malware',
  'virus',
  'trojan',
  'backdoor',
  'payload',
] as const

const DANGEROUS_FILENAME_EXTENSIONS = new Set([
  'exe', 'bat', 'cmd', 'com', 'pif', 'scr', 'vbs', 'wsf', 'wsh', 'ps1',
  'sh', 'bash', 'php', 'asp', 'jsp', 'js', 'html', 'htm', 'xml', 'sql',
])

function hasSuspiciousUploadFileName(fileName: string): boolean {
  const lower = fileName.toLowerCase()
  const dotIndex = lower.lastIndexOf('.')
  const baseName = dotIndex >= 0 ? lower.slice(0, dotIndex) : lower

  for (const segment of baseName.split('.')) {
    if (DANGEROUS_FILENAME_EXTENSIONS.has(segment)) return true
  }

  return SUSPICIOUS_FILENAME_TOKENS.some((token) => baseName.includes(token))
}

export function formatSlipFileSize(bytes: number): string {
  if (bytes >= 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }
  return `${Math.max(1, Math.round(bytes / 1024))} KB`
}

export function slipFileTooLargeMessage(actualBytes: number): string {
  return `ไฟล์ขนาด ${formatSlipFileSize(actualBytes)} ใหญ่เกินไป (สูงสุด ${SLIP_MAX_FILE_MB}MB) — ลอง crop เฉพาะส่วนสลิปหรือ screenshot ใหม่`
}

function getFileExtension(fileName: string): string {
  const dotIndex = fileName.lastIndexOf('.')
  if (dotIndex < 0) return ''
  return fileName.toLowerCase().slice(dotIndex)
}

export function validateSlipImageFileBasics(file: File): string | null {
  const fileExtension = getFileExtension(file.name)
  const hasAllowedMimeType = SLIP_ALLOWED_MIME_TYPES.includes(
    String(file.type || '').toLowerCase() as (typeof SLIP_ALLOWED_MIME_TYPES)[number],
  )
  const hasAllowedExtension = SLIP_ALLOWED_EXTENSIONS.includes(
    fileExtension as (typeof SLIP_ALLOWED_EXTENSIONS)[number],
  )

  if (!hasAllowedMimeType && !hasAllowedExtension) {
    return 'กรุณาอัปโหลดไฟล์รูปภาพเท่านั้น (JPG, PNG, GIF, WebP)'
  }

  if (fileExtension && !SLIP_ALLOWED_EXTENSIONS.includes(fileExtension as (typeof SLIP_ALLOWED_EXTENSIONS)[number])) {
    return 'นามสกุลไฟล์ไม่ถูกต้อง กรุณาอัปโหลดไฟล์รูปภาพเท่านั้น'
  }

  const lowerName = file.name.toLowerCase()
  if (hasSuspiciousUploadFileName(lowerName)) {
    return 'ชื่อไฟล์ไม่เหมาะสม กรุณาเปลี่ยนชื่อไฟล์และลองใหม่'
  }

  if (file.size < SLIP_MIN_FILE_BYTES) {
    return 'ไฟล์มีขนาดเล็กเกินไป อาจไม่ใช่สลิปที่ถูกต้อง'
  }

  return null
}

export type PrepareSlipFileResult =
  | { ok: true; file: File; compressed: boolean; originalSize: number }
  | { ok: false; message: string }

function loadImageFromFile(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file)
    const image = new Image()
    image.onload = () => {
      URL.revokeObjectURL(url)
      resolve(image)
    }
    image.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error('LOAD_FAILED'))
    }
    image.src = url
  })
}

function canvasToJpegBlob(canvas: HTMLCanvasElement, quality: number): Promise<Blob | null> {
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), 'image/jpeg', quality)
  })
}

async function compressSlipImageFile(file: File): Promise<File | null> {
  try {
    const image = await loadImageFromFile(file)
    const { width, height } = image
    if (!width || !height) return null

    const scale = Math.min(1, SLIP_MAX_IMAGE_DIMENSION / Math.max(width, height))
    const targetWidth = Math.max(1, Math.round(width * scale))
    const targetHeight = Math.max(1, Math.round(height * scale))

    const canvas = document.createElement('canvas')
    canvas.width = targetWidth
    canvas.height = targetHeight
    const context = canvas.getContext('2d')
    if (!context) return null

    context.fillStyle = '#ffffff'
    context.fillRect(0, 0, targetWidth, targetHeight)
    context.drawImage(image, 0, 0, targetWidth, targetHeight)

    const baseName = file.name.replace(/\.[^.]+$/, '') || 'slip'
    let quality = 0.88
    let blob: Blob | null = null

    while (quality >= 0.4) {
      blob = await canvasToJpegBlob(canvas, quality)
      if (!blob) return null
      if (blob.size <= SLIP_MAX_FILE_BYTES) break
      quality -= 0.08
    }

    if (!blob) return null

    return new File([blob], `${baseName}.jpg`, {
      type: 'image/jpeg',
      lastModified: Date.now(),
    })
  } catch {
    return null
  }
}

export async function prepareSlipFileForUpload(file: File): Promise<PrepareSlipFileResult> {
  const basicsError = validateSlipImageFileBasics(file)
  if (basicsError) {
    return { ok: false, message: basicsError }
  }

  const originalSize = file.size
  const shouldTryCompress =
    file.size > SLIP_MAX_FILE_BYTES || file.size > SLIP_COMPRESS_IF_LARGER_THAN_BYTES

  if (!shouldTryCompress) {
    return { ok: true, file, compressed: false, originalSize }
  }

  const compressed = await compressSlipImageFile(file)
  if (compressed) {
    if (compressed.size <= SLIP_MAX_FILE_BYTES) {
      return {
        ok: true,
        file: compressed,
        compressed: compressed.size < file.size,
        originalSize,
      }
    }
    return { ok: false, message: slipFileTooLargeMessage(compressed.size) }
  }

  if (file.size > SLIP_MAX_FILE_BYTES) {
    return {
      ok: false,
      message: `${slipFileTooLargeMessage(file.size)} — ไม่สามารถปรับขนาดอัตโนมัติได้ กรุณา crop หรือ screenshot ใหม่`,
    }
  }

  return { ok: true, file, compressed: false, originalSize }
}

export function slipCompressedMessage(originalBytes: number, nextBytes: number): string {
  return `ปรับขนาดสลิปจาก ${formatSlipFileSize(originalBytes)} เป็น ${formatSlipFileSize(nextBytes)} แล้ว`
}
